import type { ReactNode } from "react";
import { Heart, Star, ThumbsUp } from "lucide-react";

import { DatePickerField } from "@/components/DatePickerField";
import { SignaturePad } from "@/components/SignaturePad";
import {
  CORE_FORM_FIELD_KEYS,
  getBooleanValue,
  getGridMultiValue,
  getGridSingleValue,
  getNumberValue,
  getStringArrayValue,
  getStringValue,
  type AgreementFieldBlock,
  type AgreementFormBlock,
  type AgreementDateBlock,
  type AgreementFormSchema,
  type AgreementFormSection,
  type AgreementFormValues,
  type AgreementGridMultiBlock,
  type AgreementGridSingleBlock,
  type AgreementSignatureBlock
} from "@/lib/agreement-form-schema";

const DEFAULT_EMPTY_TOKEN_VALUE = "__________";

function sanitizeRichTextHtml(value: string) {
  return value
    .replace(/<\/?(script|style|iframe|object|embed|link|meta|base)[^>]*>/gi, "")
    .replace(/\s+on[a-z]+\s*=\s*(".*?"|'.*?'|[^\s>]+)/gi, "")
    .replace(/\s+(href|src)\s*=\s*(['"])\s*javascript:[\s\S]*?\2/gi, " $1=\"#\"")
    .replace(/\s+(href|src)\s*=\s*(['"])\s*data:text\/html[\s\S]*?\2/gi, " $1=\"#\"");
}

function escapeHtml(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatTokenValue(value: unknown, emptyTokenValue: string) {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || emptyTokenValue;
  }

  if (typeof value === "number") {
    return Number.isFinite(value) ? String(value) : emptyTokenValue;
  }

  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }

  if (Array.isArray(value)) {
    const entries = value
      .filter((entry): entry is string | number => typeof entry === "string" || typeof entry === "number")
      .map((entry) => String(entry).trim())
      .filter(Boolean);

    return entries.length > 0 ? entries.join(", ") : emptyTokenValue;
  }

  return emptyTokenValue;
}

function resolveDynamicText(
  template: string,
  values: AgreementFormValues,
  options?: {
    html?: boolean;
    emptyTokenValue?: string;
  }
) {
  return template.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_match, tokenKey: string) => {
    const emptyTokenValue = options?.emptyTokenValue ?? DEFAULT_EMPTY_TOKEN_VALUE;
    const resolvedValue = formatTokenValue(values[tokenKey], emptyTokenValue);
    return options?.html ? escapeHtml(resolvedValue) : resolvedValue;
  });
}

interface AgreementFormRendererProps {
  errors?: Record<string, string>;
  emptyTokenValue?: string;
  onValueChange?: (fieldKey: string, value: unknown) => void;
  readOnly?: boolean;
  readOnlyFieldKeys?: Set<string>;
  schema: AgreementFormSchema;
  sectionIds?: string[];
  values: AgreementFormValues;
}

export function AgreementFormRenderer({
  errors,
  emptyTokenValue,
  onValueChange,
  readOnly = false,
  readOnlyFieldKeys,
  schema,
  sectionIds,
  values
}: AgreementFormRendererProps) {
  const sections = sectionIds
    ? schema.sections.filter((section) => sectionIds.includes(section.id))
    : schema.sections;

  return (
    <div className="space-y-6">
      {sections.map((section, sectionIndex) => (
        <AgreementFormSectionCard
          emptyTokenValue={emptyTokenValue}
          errors={errors}
          key={section.id}
          onValueChange={onValueChange}
          readOnly={readOnly}
          readOnlyFieldKeys={readOnlyFieldKeys}
          section={section}
          sectionIndex={sectionIndex}
          totalSections={sections.length}
          values={values}
        />
      ))}
    </div>
  );
}

function AgreementFormSectionCard({
  emptyTokenValue,
  errors,
  onValueChange,
  readOnly,
  readOnlyFieldKeys,
  section,
  sectionIndex,
  totalSections,
  values
}: {
  emptyTokenValue?: string;
  errors?: Record<string, string>;
  onValueChange?: (fieldKey: string, value: unknown) => void;
  readOnly: boolean;
  readOnlyFieldKeys?: Set<string>;
  section: AgreementFormSection;
  sectionIndex: number;
  totalSections: number;
  values: AgreementFormValues;
}) {
  return (
    <section className="document-surface p-6 md:p-8">
      <div className="flex flex-col gap-3 border-b border-line/80 pb-5">
        <div className="inline-flex w-fit items-center rounded-full bg-cloud px-3 py-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-royal">
          Section {sectionIndex + 1} of {totalSections}
        </div>
        <div>
          <h3 className="legal-subtitle">{section.title}</h3>
          {section.description ? (
            <p className="legal-note mt-2 max-w-3xl">{section.description}</p>
          ) : null}
        </div>
      </div>

      <div className="mt-6 grid gap-5 md:grid-cols-2">
        {section.blocks.map((block) => (
          <div className={getBlockLayoutClass(block)} key={block.id}>
            <AgreementFormBlockView
              block={block}
              emptyTokenValue={emptyTokenValue}
              error={errors?.[block.id]}
              onValueChange={onValueChange}
              readOnly={readOnly}
              readOnlyFieldKeys={readOnlyFieldKeys}
              values={values}
            />
          </div>
        ))}
      </div>
    </section>
  );
}

function getBlockLayoutClass(block: AgreementFormBlock) {
  if (
    block.type === "heading" ||
    block.type === "paragraph" ||
    block.type === "multimodalText" ||
    block.type === "longText" ||
    block.type === "multipleChoice" ||
    block.type === "checkboxes" ||
    block.type === "linearScale" ||
    block.type === "rating" ||
    block.type === "gridSingle" ||
    block.type === "gridMulti" ||
    block.type === "signature"
  ) {
    return "md:col-span-2";
  }

  return "md:col-span-1";
}

function AgreementFormBlockView({
  block,
  emptyTokenValue,
  error,
  onValueChange,
  readOnly,
  readOnlyFieldKeys,
  values
}: {
  block: AgreementFormBlock;
  emptyTokenValue?: string;
  error?: string;
  onValueChange?: (fieldKey: string, value: unknown) => void;
  readOnly: boolean;
  readOnlyFieldKeys?: Set<string>;
  values: AgreementFormValues;
}) {
  if (block.type === "heading") {
    return (
      <h4 className="legal-subtitle">
        {resolveDynamicText(block.text, values, { emptyTokenValue })}
      </h4>
    );
  }

  if (block.type === "paragraph") {
    return (
      <p className="legal-prose">
        {resolveDynamicText(block.text, values, { emptyTokenValue })}
      </p>
    );
  }

  if (block.type === "multimodalText") {
    return (
      <div className="rounded-[18px] border border-line bg-white p-5 md:p-6">
        <div
          className="legal-rich-text max-w-none"
          dangerouslySetInnerHTML={{
            __html: sanitizeRichTextHtml(
              resolveDynamicText(block.richTextHtml, values, { html: true, emptyTokenValue })
            )
          }}
        />
        {block.attachments.length > 0 ? (
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            {block.attachments.map((attachment) => (
              <figure
                className="overflow-hidden rounded-[18px] border border-line bg-cloud"
                key={attachment.id}
              >
                {attachment.url ? (
                  <img
                    alt={attachment.alt}
                    className="h-56 w-full object-cover"
                    src={attachment.url}
                  />
                ) : (
                  <div className="flex h-56 items-center justify-center px-6 text-center text-sm text-slate">
                    Add an image URL in the form builder to preview multimodal content here.
                  </div>
                )}
                {attachment.caption ? (
                  <figcaption className="border-t border-line/70 px-4 py-3 text-sm text-slate">
                    {attachment.caption}
                  </figcaption>
                ) : null}
              </figure>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  if (block.type === "signature") {
    return (
      <SignatureBlockView
        block={block}
        error={error}
        onValueChange={onValueChange}
        readOnly={readOnly}
        values={values}
      />
    );
  }

  return (
    <FieldBlockView
      block={block}
      error={error}
      onValueChange={onValueChange}
      readOnly={readOnly}
      readOnlyFieldKeys={readOnlyFieldKeys}
      values={values}
    />
  );
}

function FieldBlockView({
  block,
  error,
  onValueChange,
  readOnly,
  readOnlyFieldKeys,
  values
}: {
  block: AgreementFieldBlock;
  error?: string;
  onValueChange?: (fieldKey: string, value: unknown) => void;
  readOnly: boolean;
  readOnlyFieldKeys?: Set<string>;
  values: AgreementFormValues;
}) {
  const fieldReadOnly = readOnly || Boolean(readOnlyFieldKeys?.has(block.fieldKey));

  if (block.type === "date") {
    const dateInputAttributes = getDateInputAttributes(block, values);

    return (
      <FieldShell block={block} error={error}>
        <DatePickerField
          ariaLabel={block.label}
          disabled={fieldReadOnly}
          max={dateInputAttributes.max}
          min={dateInputAttributes.min}
          onChange={(nextValue) => onValueChange?.(block.fieldKey, nextValue)}
          placeholder="dd/mm/yyyy"
          value={getStringValue(values, block.fieldKey)}
        />
      </FieldShell>
    );
  }

  if (block.type === "shortText" || block.type === "email" || block.type === "time") {
    const isFixedReadonlyField = block.fieldKey === CORE_FORM_FIELD_KEYS.workingHours;
    const currentValue = getStringValue(values, block.fieldKey);

    return (
      <FieldShell block={block} error={error}>
        <input
          className="input-field"
          disabled={readOnly}
          inputMode={
            block.fieldKey === CORE_FORM_FIELD_KEYS.stipendAmount ? "numeric" : undefined
          }
          maxLength={block.validation?.maxLength}
          onChange={(event) => {
            const nextValue =
              block.fieldKey === CORE_FORM_FIELD_KEYS.stipendAmount
                ? event.target.value.replace(/\D/g, "").slice(0, 5)
                : event.target.value;
            onValueChange?.(block.fieldKey, nextValue);
          }}
          placeholder={"placeholder" in block ? block.placeholder : undefined}
          readOnly={isFixedReadonlyField}
          type={block.type === "shortText" ? "text" : block.type}
          value={currentValue}
        />
      </FieldShell>
    );
  }

  if (block.type === "longText") {
    return (
      <FieldShell block={block} error={error}>
        <textarea
          className="input-field min-h-28"
          disabled={fieldReadOnly}
          onChange={(event) => onValueChange?.(block.fieldKey, event.target.value)}
          placeholder={block.placeholder}
          rows={block.rows ?? 4}
          value={getStringValue(values, block.fieldKey)}
        />
      </FieldShell>
    );
  }

  if (block.type === "dropdown") {
    return (
      <FieldShell block={block} error={error}>
        <select
          className="input-field"
          disabled={fieldReadOnly}
          onChange={(event) => onValueChange?.(block.fieldKey, event.target.value)}
          value={getStringValue(values, block.fieldKey)}
        >
          <option value="">Select an option</option>
          {block.options.map((option) => (
            <option key={option.id} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </FieldShell>
    );
  }

  if (block.type === "multipleChoice") {
    return (
      <FieldShell block={block} error={error}>
        <div className="grid gap-3 sm:grid-cols-2">
          {block.options.map((option) => {
            const checked = getStringValue(values, block.fieldKey) === option.value;

            return (
              <label
                className="flex h-full items-start gap-3 rounded-[18px] border border-line bg-cloud px-4 py-4"
                key={option.id}
              >
                <input
                  checked={checked}
                  className="mt-1 h-4 w-4"
                  disabled={fieldReadOnly}
                  name={block.fieldKey}
                  onChange={() => onValueChange?.(block.fieldKey, option.value)}
                  type="radio"
                />
                <span className="text-sm leading-7 text-slate">
                  <span className="block font-semibold text-ink">{option.label}</span>
                  {option.description ? (
                    <span className="mt-1 block text-slate">{option.description}</span>
                  ) : null}
                </span>
              </label>
            );
          })}
        </div>
      </FieldShell>
    );
  }

  if (block.type === "checkboxes") {
    const selectedValues = getStringArrayValue(values, block.fieldKey);
    return (
      <FieldShell block={block} error={error}>
        <div className="grid gap-3 sm:grid-cols-2">
          {block.options.map((option) => {
            const checked = selectedValues.includes(option.value);

            return (
              <label
                className="flex h-full items-start gap-3 rounded-[18px] border border-line bg-cloud px-4 py-4"
                key={option.id}
              >
                <input
                  checked={checked}
                  className="mt-1 h-4 w-4"
                  disabled={fieldReadOnly}
                  onChange={(event) => {
                    const nextValues = event.target.checked
                      ? [...selectedValues, option.value]
                      : selectedValues.filter((entry) => entry !== option.value);
                    onValueChange?.(block.fieldKey, nextValues);
                  }}
                  type="checkbox"
                />
                <span className="text-sm leading-7 text-slate">
                  <span className="block font-semibold text-ink">{option.label}</span>
                  {option.description ? (
                    <span className="mt-1 block text-slate">{option.description}</span>
                  ) : null}
                </span>
              </label>
            );
          })}
        </div>
      </FieldShell>
    );
  }

  if (block.type === "linearScale") {
    const scaleValues = Array.from({ length: block.max - block.min + 1 }, (_, index) => block.min + index);
    return (
      <FieldShell block={block} error={error}>
        <div className="rounded-[18px] border border-line bg-cloud px-4 py-4">
          <div className="grid gap-3 md:grid-cols-[80px_minmax(0,1fr)_80px] md:items-center">
            <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate/70">
              {block.minLabel ?? block.min}
            </span>
            <div className="grid grid-cols-5 gap-2">
              {scaleValues.map((value) => (
                <button
                  className={`rounded-2xl border px-3 py-3 text-sm font-semibold transition ${
                    getNumberValue(values, block.fieldKey) === value
                      ? "border-royal bg-royal text-white"
                      : "border-line/70 bg-white text-ink"
                  }`}
                  disabled={fieldReadOnly}
                  key={value}
                  onClick={() => onValueChange?.(block.fieldKey, value)}
                  type="button"
                >
                  {value}
                </button>
              ))}
            </div>
            <span className="text-right text-xs font-semibold uppercase tracking-[0.18em] text-slate/70">
              {block.maxLabel ?? block.max}
            </span>
          </div>
        </div>
      </FieldShell>
    );
  }

  if (block.type === "rating") {
    const iconMap = {
      heart: Heart,
      star: Star,
      thumb: ThumbsUp
    } as const;
    const Icon = iconMap[block.icon ?? "star"];
    const selectedValue = getNumberValue(values, block.fieldKey);
    return (
      <FieldShell block={block} error={error}>
        <div className="flex flex-wrap gap-3 rounded-[18px] border border-line bg-cloud px-4 py-4">
          {Array.from({ length: block.max }, (_, index) => index + 1).map((value) => (
            <button
              className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl border transition ${
                selectedValue >= value
                  ? "border-amber-200 bg-amber-50 text-amber-700"
                  : "border-line/70 bg-white text-slate"
              }`}
              disabled={fieldReadOnly}
              key={value}
              onClick={() => onValueChange?.(block.fieldKey, value)}
              type="button"
            >
              <Icon className="h-5 w-5" />
            </button>
          ))}
        </div>
      </FieldShell>
    );
  }

  if (block.type === "gridSingle") {
    return (
      <FieldShell block={block} error={error}>
        <GridSingleView
          block={block}
          onValueChange={onValueChange}
          readOnly={fieldReadOnly}
          values={values}
        />
      </FieldShell>
    );
  }

  return (
    <FieldShell block={block} error={error}>
      <GridMultiView
        block={block}
        onValueChange={onValueChange}
        readOnly={fieldReadOnly}
        values={values}
      />
    </FieldShell>
  );
}

function FieldShell({
  block,
  children,
  error
}: {
  block: AgreementFieldBlock;
  children: ReactNode;
  error?: string;
}) {
  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-2">
        <span className="field-label">{block.label}</span>
        {block.required ? <span className="text-sm font-semibold text-rose-600">*</span> : null}
      </div>
      {block.description ? <p className="legal-note">{block.description}</p> : null}
      {children}
      {block.helperText ? <p className="legal-note text-xs">{block.helperText}</p> : null}
      {error ? <p className="text-sm font-medium text-rose-700">{error}</p> : null}
    </div>
  );
}

function SignatureBlockView({
  block,
  error,
  onValueChange,
  readOnly,
  values
}: {
  block: AgreementSignatureBlock;
  error?: string;
  onValueChange?: (fieldKey: string, value: unknown) => void;
  readOnly: boolean;
  values: AgreementFormValues;
}) {
  const combinedConsentLabel = buildCombinedConsentLabel(block);
  const allConsentsAccepted =
    getBooleanValue(values, CORE_FORM_FIELD_KEYS.acceptedTerms) &&
    getBooleanValue(values, CORE_FORM_FIELD_KEYS.acceptedConfidentiality) &&
    getBooleanValue(values, CORE_FORM_FIELD_KEYS.acceptedElectronicSignature);
  const consentErrorLabels = new Set([
    block.acceptedTermsLabel,
    block.acceptedConfidentialityLabel,
    block.acceptedElectronicSignatureLabel
  ]);
  const displayError = error && consentErrorLabels.has(error) ? combinedConsentLabel : error;

  return (
    <div className="space-y-4">
      <SignaturePad
        disabled={readOnly}
        onChange={(value) => onValueChange?.(CORE_FORM_FIELD_KEYS.signatureDataUrl, value)}
        onSignerNameChange={(value) => onValueChange?.(CORE_FORM_FIELD_KEYS.signatureName, value)}
        signedAt={getStringValue(values, CORE_FORM_FIELD_KEYS.signedAt)}
        signerName={getStringValue(values, CORE_FORM_FIELD_KEYS.signatureName)}
        value={getStringValue(values, CORE_FORM_FIELD_KEYS.signatureDataUrl)}
      />

      <div className="grid gap-3">
        <ConsentCheckbox
          checked={allConsentsAccepted}
          disabled={readOnly}
          label={combinedConsentLabel}
          onChange={(checked) =>
            updateSignatureConsents(onValueChange, checked)
          }
        />
      </div>

      {displayError ? <p className="text-sm font-medium text-rose-700">{displayError}</p> : null}
    </div>
  );
}

function buildCombinedConsentLabel(block: AgreementSignatureBlock) {
  return [
    block.acceptedTermsLabel,
    block.acceptedConfidentialityLabel,
    block.acceptedElectronicSignatureLabel
  ]
    .map((label) => label.trim())
    .filter(Boolean)
    .join(" ");
}

function updateSignatureConsents(
  onValueChange: ((fieldKey: string, value: unknown) => void) | undefined,
  checked: boolean
) {
  onValueChange?.(CORE_FORM_FIELD_KEYS.acceptedTerms, checked);
  onValueChange?.(CORE_FORM_FIELD_KEYS.acceptedConfidentiality, checked);
  onValueChange?.(CORE_FORM_FIELD_KEYS.acceptedElectronicSignature, checked);
}

function getDateInputAttributes(block: AgreementDateBlock, values: AgreementFormValues) {
  const attributes: {
    max?: string;
    min?: string;
  } = {};

  if (block.fieldKey === CORE_FORM_FIELD_KEYS.endDate) {
    const startDate = getStringValue(values, CORE_FORM_FIELD_KEYS.startDate).trim();
    attributes.min = startDate || undefined;
    return attributes;
  }

  return attributes;
}

function ConsentCheckbox({
  checked,
  disabled,
  label,
  onChange
}: {
  checked: boolean;
  disabled: boolean;
  label: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 rounded-[18px] border border-line bg-cloud px-4 py-4">
      <input
        checked={checked}
        className="mt-1 h-4 w-4 rounded border-line"
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
        type="checkbox"
      />
      <span className="text-sm leading-7 text-slate">{label}</span>
    </label>
  );
}

function GridSingleView({
  block,
  onValueChange,
  readOnly,
  values
}: {
  block: AgreementGridSingleBlock;
  onValueChange?: (fieldKey: string, value: unknown) => void;
  readOnly: boolean;
  values: AgreementFormValues;
}) {
  const currentValue = getGridSingleValue(values, block.fieldKey);

  return (
    <div className="overflow-x-auto rounded-[18px] border border-line bg-white">
      <table className="min-w-full border-collapse">
        <thead className="bg-cloud">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate/70">
              Row
            </th>
            {block.columns.map((column) => (
              <th
                className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-[0.18em] text-slate/70"
                key={column.id}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row) => (
            <tr className="border-t border-line/70" key={row.id}>
              <td className="px-4 py-4 text-sm font-medium text-ink">{row.label}</td>
              {block.columns.map((column) => (
                <td className="px-4 py-4 text-center" key={column.id}>
                  <input
                    checked={currentValue[row.id] === column.id}
                    disabled={readOnly}
                    name={`${block.fieldKey}-${row.id}`}
                    onChange={() =>
                      onValueChange?.(block.fieldKey, {
                        ...currentValue,
                        [row.id]: column.id
                      })
                    }
                    type="radio"
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function GridMultiView({
  block,
  onValueChange,
  readOnly,
  values
}: {
  block: AgreementGridMultiBlock;
  onValueChange?: (fieldKey: string, value: unknown) => void;
  readOnly: boolean;
  values: AgreementFormValues;
}) {
  const currentValue = getGridMultiValue(values, block.fieldKey);

  return (
    <div className="overflow-x-auto rounded-[18px] border border-line bg-white">
      <table className="min-w-full border-collapse">
        <thead className="bg-cloud">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.18em] text-slate/70">
              Row
            </th>
            {block.columns.map((column) => (
              <th
                className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-[0.18em] text-slate/70"
                key={column.id}
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row) => (
            <tr className="border-t border-line/70" key={row.id}>
              <td className="px-4 py-4 text-sm font-medium text-ink">{row.label}</td>
              {block.columns.map((column) => {
                const rowSelections = currentValue[row.id] ?? [];
                const checked = rowSelections.includes(column.id);

                return (
                  <td className="px-4 py-4 text-center" key={column.id}>
                    <input
                      checked={checked}
                      disabled={readOnly}
                      onChange={(event) => {
                        const nextRowSelections = event.target.checked
                          ? [...rowSelections, column.id]
                          : rowSelections.filter((entry) => entry !== column.id);

                        onValueChange?.(block.fieldKey, {
                          ...currentValue,
                          [row.id]: nextRowSelections
                        });
                      }}
                      type="checkbox"
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
